import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { InternalAxiosRequestConfig } from 'axios';

interface PayloadIdentity {
  dateCreated: string;
  issuer: unknown;
  primary: 'true' | 'false';
  providerName: string;
  providerType: string;
  userId: string;
}

interface IdTokenPayload {
  at_hash: string;
  aud: string;
  auth_time: number;
  'cognito:groups': string[];
  'cognito:username': string;
  'custom:SysRole': string;
  'custom:preferredMode': string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  iat: number;
  identities: PayloadIdentity[];
  iss: string;
  jti: string;
  middle_name: string;
  origin_jti: string;
  sub: string;
  token_use: string;
}

export type IdToken = CognitoIdToken & { payload: IdTokenPayload };

export async function getIdToken() {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken() as IdToken;
  } catch (err) {
    console.error('Failed to get JWT token', err);
    throw err;
  }
}

export async function getJwtToken() {
  try {
    // Calling Auth.currentSession automatically refreshes the id/access tokens using the refresh token
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error('Failed to get JWT token', err);
    throw err;
  }
}

export type SysRole = {
  role: string;
  fadCode: string;
};

export type CognitoUser = {
  signInUserSession: {
    idToken: IdToken;
  };
  storage?: {
    fadcode?: string;
  };
};

export const extractSysRoleFromIdToken = (idToken: IdToken): SysRole[] => {
  try {
    return JSON.parse(
      decodeURIComponent(idToken.payload['custom:SysRole']).replaceAll('+', ''),
    ) as SysRole[];
  } catch (e) {
    console.error('Failed to parse SysRole from idToken: ', e);
    return [];
  }
};

export const determineSysRole = (user: CognitoUser, extractedRoles: SysRole[]): SysRole[] => {
  if (!user.storage?.fadcode) {
    return extractedRoles;
  }

  const formattedExistingFadCode = user.storage.fadcode.replace(/["']/g, '');
  const filteredRoles = extractedRoles.filter((item) => item.fadCode === formattedExistingFadCode);

  return filteredRoles.length > 0 ? filteredRoles : extractedRoles;
};

// Forces the user into the sign-in flow if they are not authenticated
export const authenticateUser = async (clearAppData: () => void) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const username: string | undefined =
      user?.signInUserSession?.idToken?.payload?.name ||
      user?.signInUserSession?.idToken?.payload?.given_name;
    const smartId: string | undefined =
      user?.signInUserSession.idToken.payload.identities[0].userId;

    const extractedRoles = extractSysRoleFromIdToken(user?.signInUserSession.idToken);
    const sysRole = determineSysRole(user, extractedRoles);

    return {
      username,
      smartId,
      sysRole,
    };
  } catch (e) {
    clearAppData(); // Clear all localStorage and redux data so we don't reuse the previously persisted fadcode when the 30-day refresh token expires
    Auth.federatedSignIn({
      customProvider: process.env.NEXT_PUBLIC_USER_PROVIDER ?? 'forgerock',
    });
  }
};

export const requestTokenRefreshInterceptor = [
  // onFulfilled
  async (config: InternalAxiosRequestConfig) => {
    // Get/refresh the jwt token
    const jwtToken = await getJwtToken();
    config.headers.Authorization = `Bearer ${jwtToken}`;
    config.headers.Accept = 'application/json';
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  // onRejected
  // Has to be `any` type as this is the type Axios expects, and it does not accept generic types for the error type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (error: any) => Promise.reject(error),
];
